import axios from 'axios';
import { clearAuth, getLastTenant, getToken, getUser } from './auth';
import { getSimulate } from './simulate';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Development authentication headers (fallback if no token)
// In production, these will come from an auth token/session
const DEV_TENANT_CODE = process.env.REACT_APP_DEFAULT_TENANT_CODE || 'DEV';
const DEV_USERNAME = process.env.REACT_APP_DEFAULT_DEV_USERNAME || 'dev_george';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// First path segment that is an app route, not a tenant code (must match useTenantFromUrl.js)
const APP_PATH_SEGMENTS = new Set([
  'clients', 'client', 'providers', 'provider', 'payees', 'payee', 'contacts', 'contact',
  'schools', 'school', 'reports', 'referrals', 'referral', 'dashboard', 'work-tasks', 'settings', 'super-admin', 'login',
  'at-packets', 'file-reviews', 'my-checklists', 'client-services', 'transfers', 'training'
]);

function getTenantCodeFromUrl() {
  if (typeof window === 'undefined' || !window.location?.pathname) return null;
  const first = window.location.pathname.split('/').filter(Boolean)[0];
  if (!first || APP_PATH_SEGMENTS.has(first)) return null;
  return first;
}

function getTenantCodeFallback() {
  const userTenant = getUser()?.tenant?.code;
  const lastTenant = getLastTenant();
  return userTenant || lastTenant || null;
}

// Request interceptor: Add JWT token if available, otherwise use dev headers (dev only)
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    
    if (token) {
      // Use JWT token authentication
      config.headers['Authorization'] = `Bearer ${token}`;
      // When URL is /:tenantCode/..., send that tenant so backend uses it (e.g. /CFC4/client/123/documents → CFC4)
      const urlTenant = getTenantCodeFromUrl();
      const fallbackTenant = getTenantCodeFallback();
      const effectiveTenant = urlTenant || fallbackTenant;
      if (effectiveTenant) config.headers['x-tenant-code'] = effectiveTenant;
      // When "View as role" is active, tell backend to apply that role's permissions
      const sim = getSimulate();
      if (sim?.userId) {
        config.headers['X-Simulate-User-Id'] = String(sim.userId);
      } else if (sim?.roleId) {
        config.headers['X-Simulate-Role'] = String(sim.roleId);
      }
    } else {
      // Fallback to dev headers (development only)
      // In production, this should be disabled for security
      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           !process.env.NODE_ENV || 
                           process.env.REACT_APP_ALLOW_DEV_HEADERS === 'true';
      
      if (isDevelopment) {
        const urlTenant = getTenantCodeFromUrl();
        config.headers['x-tenant-code'] = urlTenant || DEV_TENANT_CODE;
        config.headers['x-dev-user'] = DEV_USERNAME;
      }
      // In production without token, no headers are added (will require authentication)
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Guard so only the first 401 triggers the redirect — prevents a burst of
// simultaneous expired-token responses from each calling location.replace.
let redirectingToLogin = false;

// Response interceptor: Handle blob errors (when server returns JSON error but client expects blob)
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Token expired or invalid — clear local auth and redirect to login
    if (error.response?.status === 401) {
      if (!redirectingToLogin) {
        redirectingToLogin = true;
        clearAuth();
        window.location.replace('/login');
      }
      return Promise.reject(error);
    }

    // If error response is a blob but should be JSON, convert it
    if (error.config && error.config.responseType === 'blob' && error.response && error.response.data) {
      const contentType = error.response.headers['content-type'] || error.response.headers['Content-Type'] || '';
      if (contentType.includes('application/json')) {
        try {
          // Convert blob to text, then parse as JSON
          const reader = new FileReader();
          const text = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsText(error.response.data);
          });
          const json = JSON.parse(text);
          error.response.data = json;
        } catch (parseError) {
          // If parsing fails, keep original error
          console.error('Failed to parse error response:', parseError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// Client API
// getAll(params): { page, pageSize, q, ei, status, assigned, assigned_staff_id }. Returns { data: clients[], total }.
export const clientAPI = {
  getAll: (params = {}) => api.get('/clients', { params }),
  /** Distinct assigned SCs for My Clients filter (matches status + optional q/ei, not all tenant staff). */
  getAssignedStaffFilterOptions: (params = {}) =>
    api.get('/clients/assigned-staff-filter-options', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  /** Merge Client Letters template for this client (subject + body with {{TAG}} filled). */
  renderEmailLetter: (clientId, { template_code, language = 'EN' }) =>
    api.post(`/clients/${clientId}/email-letter-render`, { template_code, language }),
  getAsqForm: (id, params = {}) => api.get(`/clients/${id}/asq-form`, { params }),
  getReferralInsuranceSummary: (id) => api.get(`/clients/${id}/referral-insurance-summary`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getAssignmentHistory: (id) => api.get(`/clients/${id}/assignment-history`),
};

// Client work tasks (client-scoped list, legacy Work Tasks)
// Work tasks are referral-scoped (like checklists). Use referralId for list/create/update/delete.
export const clientWorkTasksAPI = {
  getWorkTypes: () => api.get('/clients/work-task-work-types'),
  getTaskTypes: () => api.get('/clients/work-task-task-types'),
  getByReferralId: (referralId, config) => api.get(`/referrals/${referralId}/work-tasks`, config),
  create: (referralId, data) => api.post(`/referrals/${referralId}/work-tasks`, data),
  update: (referralId, taskId, data) => api.put(`/referrals/${referralId}/work-tasks/${taskId}`, data),
  delete: (referralId, taskId) => api.delete(`/referrals/${referralId}/work-tasks/${taskId}`),
  /** Dashboard: count open work tasks (requested, not received) for open referrals */
  getOpenCount: (params) => api.get('/referrals/work-tasks-open-count', { params }),
  /** Dashboard: list open work tasks for panel */
  getOpenList: (params) => api.get('/referrals/work-tasks-open-list', { params }),
};

/** Not-client-specific My To-Dos (left nav); general only, no client/referral/work-task fields */
export const personalMyTodoAPI = {
  list: (config) => api.get('/my-todos/personal', config),
  create: (data) => api.post('/my-todos/personal', data),
  update: (id, data) => api.put(`/my-todos/personal/${id}`, data),
  delete: (id) => api.delete(`/my-todos/personal/${id}`),
};

// Provider Type API
export const providerTypeAPI = {
  getAll: () => api.get('/provider-types'),
  getById: (id) => api.get(`/provider-types/${id}`),
  create: (data) => api.post('/provider-types', data),
  update: (id, data) => api.put(`/provider-types/${id}`, data),
  delete: (id) => api.delete(`/provider-types/${id}`),
};

// Payee API. getAll(params): { page, pageSize, q }. Returns { data, total }.
export const payeeAPI = {
  getAll: (params = {}) => api.get('/payees', { params }),
  getById: (id) => api.get(`/payees/${id}`),
  create: (data) => api.post('/payees', data),
  update: (id, data) => api.put(`/payees/${id}`, data),
  delete: (id) => api.delete(`/payees/${id}`),
  search: (query) => api.get(`/payees/search?q=${encodeURIComponent(query)}`),
  /** One row per payee + office (street, city) for provider billing picker */
  searchOffices: (query) => api.get(`/payees/search-offices?q=${encodeURIComponent(query)}`),
};

// Provider API. getAll(params): { page, pageSize, q }. Returns { data, total }.
export const providerAPI = {
  getAll: (params = {}) => api.get('/providers', { params }),
  getById: (id) => api.get(`/providers/${id}`),
  getSites: () => api.get('/providers/sites'),
  create: (data) => api.post('/providers', data),
  update: (id, data) => api.put(`/providers/${id}`, data),
  /** Link provider to an existing site row that belongs to their payee (see payee offices). */
  addPracticeSite: (id, body) => api.post(`/providers/${id}/practice-sites`, body),
  delete: (id) => api.delete(`/providers/${id}`),
  reactivate: (id) => api.post(`/providers/${id}/reactivate`),
  getByType: (typeCode) => api.get(`/providers/type/${typeCode}`),
  getByTypeSites: (typeCode) => api.get(`/providers/type/${typeCode}/sites`),
  search: (q) => api.get('/providers/search', { params: { q: q || '' } }),
  /** Provider + office rows for Provider Team (debounced search; limit default 25). Optional { signal } for AbortController. */
  searchSiteOptions: (q, params = {}) =>
    api.get('/providers/site-options/search', {
      params: {
        q: q || '',
        limit: params.limit ?? 25,
        ...(params.typeCode ? { typeCode: params.typeCode } : {}),
      },
      signal: params.signal,
    }),
};

// Contact API. getAll(params): { page, pageSize, q }. Returns { data, total }.
export const contactAPI = {
  getAll: (params = {}) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  getRelatedClients: (id) => api.get(`/contacts/${id}/related-clients`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// School (district) API. getAll(params): { page, pageSize, q }. Returns { data, total }.
export const schoolAPI = {
  getAll: (params = {}) => api.get('/schools', { params }),
  getById: (id) => api.get(`/schools/${id}`),
  create: (data) => api.post('/schools', data),
  update: (id, data) => api.put(`/schools/${id}`, data),
  delete: (id) => api.delete(`/schools/${id}`),
  getContactTypes: () => api.get('/schools/contact-types'),
  getContacts: (schoolId) => api.get(`/schools/${schoolId}/contacts`),
  createContact: (schoolId, data) => api.post(`/schools/${schoolId}/contacts`, data),
  updateContact: (schoolId, contactId, data) => api.patch(`/schools/${schoolId}/contacts/${contactId}`, data),
  deleteContact: (schoolId, contactId) => api.delete(`/schools/${schoolId}/contacts/${contactId}`),
};

// Global Search API
export const globalSearchAPI = {
  search: (params = {}) => api.get('/search/global', { params }),
};

// Template API
export const templateAPI = {
  getAll: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/templates${queryString ? `?${queryString}` : ''}`);
  },
};

// Template Admin API (Document Generation specialist – templates.manage)
export const templateAdminAPI = {
  listAdmin: () => api.get('/templates/admin'),
  register: (files, category, options = {}) => {
    const formData = new FormData();
    if (Array.isArray(files)) {
      files.forEach((f) => formData.append('files', f));
    } else if (files) {
      formData.append('files', files);
    }
    formData.append('category', category);
    formData.append('language', options.language || 'EN');
    if (options.minAgeMonths !== undefined && options.minAgeMonths !== '') formData.append('minAgeMonths', String(options.minAgeMonths));
    if (options.maxAgeMonths !== undefined && options.maxAgeMonths !== '') formData.append('maxAgeMonths', String(options.maxAgeMonths));
    if (options.promptTags != null && options.promptTags !== '') {
      const tags = Array.isArray(options.promptTags) ? options.promptTags : String(options.promptTags).split(/[,\s]+/).map((t) => t.trim()).filter(Boolean);
      formData.append('promptTags', tags.join(','));
    }
    if (options.defaultFolderKey) formData.append('defaultFolderKey', String(options.defaultFolderKey).trim());
    return api.post('/templates/register', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  setEnabled: (id, enabled) => api.patch(`/templates/${id}`, { enabled }),
  updateTemplate: (id, data) => api.patch(`/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/templates/${id}`),
  getTagReference: () => api.get('/templates/tag-reference'),
  /** Download template file (blob). Use responseType blob; parse filename from Content-Disposition or pass template.fileName. */
  download: (id) => api.get(`/templates/${id}/download`, { responseType: 'blob' }),
};

// Document Generation API
export const documentAPI = {
  generate: (clientId, templateId) => {
    return api.post('/documents/generate', { clientId, templateId }, {
      responseType: 'blob', // Important: receive binary data
    });
  },
  // New GET endpoint for client-scoped generation
  // prompts: optional { INTAKE_DATE, CL_PREMATURE_WEEK, ... } for ASQ and other templates (INTAKE_DATE output MM-DD-YYYY)
  // providerId: optional; when set, fills DR_* (chosen doctor). providerSiteId: optional payee_sites.id (or legacy provider_sites.id) for that office's fax/phone/address.
  generateForClient: (clientId, templateId, prompts = {}, providerId, providerSiteId) => {
    const params = new URLSearchParams({ templateId });
    Object.entries(prompts).forEach(([key, val]) => {
      if (val != null) params.append(key, String(val));
    });
    if (providerId) params.set('providerId', providerId);
    if (providerSiteId) params.set('providerSiteId', providerSiteId);
    return api.get(`/documents/client/${clientId}/generate?${params.toString()}`, {
      responseType: 'blob', // Important: receive binary data
    });
  },
  // AT packet document generation (template must have category ATPacket)
  generateForAtPacket: (packetId, templateId) => {
    return api.get(`/documents/at-packet/${packetId}/generate?templateId=${templateId}`, {
      responseType: 'blob',
    });
  },
  /** Data correction letters; template must be category DataCorrection or Other + prompt tag dataCorrection */
  generateForDataCorrection: (correctionId, templateId) => {
    const q = new URLSearchParams({ templateId: String(templateId) });
    return api.get(`/documents/data-correction/${correctionId}/generate?${q.toString()}`, {
      responseType: 'blob',
    });
  },
};

// AT Packets API (feature-gated by tenant; 404 when disabled)
export const atPacketAPI = {
  list: (params = {}) => api.get('/at-packets', { params }),
  listPendingReceiptItems: (params = {}) => api.get('/at-packets/pending-receipt-items', { params }),
  getById: (id) => api.get(`/at-packets/${id}`),
  create: (data) => api.post('/at-packets', data),
  update: (id, data) => api.patch(`/at-packets/${id}`, data),
  delete: (id) => api.delete(`/at-packets/${id}`),
  getItems: (packetId) => api.get(`/at-packets/${packetId}/items`),
  addItem: (packetId, data) => api.post(`/at-packets/${packetId}/items`, data),
  updateItem: (packetId, itemId, data) => api.patch(`/at-packets/${packetId}/items/${itemId}`, data),
  deleteItem: (packetId, itemId) => api.delete(`/at-packets/${packetId}/items/${itemId}`),
  getStatuses: () => api.get('/at-packets/statuses'),
  getInsuranceTypes: () => api.get('/at-packets/insurance-types'),
  getDsccStatuses: () => api.get('/at-packets/dscc-statuses'),
  getItemTypes: () => api.get('/at-packets/item-types'),
  getItemNames: () => api.get('/at-packets/item-names'),
  /** Optional server-side send; preview-mode UI uses mailto (“Open in email”) instead. */
  sendExternalStatusEmailNow: (id) => api.post(`/at-packets/${id}/send-external-status-email`, {}),
};

// Support Cases API (in-app bug / question / feature-request tickets)
export const supportCaseAPI = {
  list: (params = {}) => api.get('/support-cases', { params }),
  getById: (id) => api.get(`/support-cases/${id}`),
  create: (data) => api.post('/support-cases', data),
  listTimeline: (id) => api.get(`/support-cases/${id}/timeline`),
  triage: (id, data) => api.patch(`/support-cases/${id}/triage`, data),
  listAgents: () => api.get('/support-cases/agents'),
  listComments: (id) => api.get(`/support-cases/${id}/comments`),
  addComment: (id, data) => api.post(`/support-cases/${id}/comments`, data),
  listAttachments: (id) => api.get(`/support-cases/${id}/attachments`),
  uploadAttachment: (id, file, commentId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (commentId) formData.append('comment_id', commentId);
    return api.post(`/support-cases/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  attachmentBlob: (id, attId) =>
    api.get(`/support-cases/${id}/attachments/${attId}/view`, { responseType: 'blob' }),
  deleteAttachment: (id, attId) => api.delete(`/support-cases/${id}/attachments/${attId}`),
  deleteCase: (id) => api.delete(`/support-cases/${id}`),
};

// Support Console (cross-tenant case-worker) API — super-admin only.
export const supportAgentAPI = {
  list: (params = {}) => api.get('/support-agent/cases', { params }),
  listAgents: () => api.get('/support-agent/agents'),
  listReleases: () => api.get('/support-agent/releases'),
  createRelease: (data) => api.post('/support-agent/releases', data),
  getById: (id) => api.get(`/support-agent/cases/${id}`),
  update: (id, data) => api.patch(`/support-agent/cases/${id}`, data),
  listTimeline: (id) => api.get(`/support-agent/cases/${id}/timeline`),
  listComments: (id) => api.get(`/support-agent/cases/${id}/comments`),
  addComment: (id, data) => api.post(`/support-agent/cases/${id}/comments`, data),
  listAttachments: (id) => api.get(`/support-agent/cases/${id}/attachments`),
  uploadAttachment: (id, file, commentId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (commentId) formData.append('comment_id', commentId);
    return api.post(`/support-agent/cases/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  attachmentBlob: (id, attId) =>
    api.get(`/support-agent/cases/${id}/attachments/${attId}/view`, { responseType: 'blob' }),
  deleteAttachment: (id, attId) => api.delete(`/support-agent/cases/${id}/attachments/${attId}`),
  listCommits: (id) => api.get(`/support-agent/cases/${id}/commits`),
  addCommit: (id, data) => api.post(`/support-agent/cases/${id}/commits`, data),
  deleteCommit: (id, commitId) => api.delete(`/support-agent/cases/${id}/commits/${commitId}`),
  deleteCase: (id) => api.delete(`/support-agent/cases/${id}`),
};

// Public avatar URL for a user (served by id). Optional version busts the cache.
export const avatarUrl = (userId, version) => {
  if (!userId && userId !== 0) return null;
  const base = `${API_BASE_URL}/users/${userId}/avatar`;
  return version ? `${base}?v=${encodeURIComponent(version)}` : base;
};

// Current user's own avatar management.
export const meAPI = {
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/me/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAvatar: () => api.delete('/me/avatar'),
};

// Client Provider Services (Team) API – team is per referral
export const clientProviderServiceAPI = {
  getByReferralId: (referralId) => api.get(`/referrals/${referralId}/provider-services`),
  getByClientId: (clientId) => api.get(`/client-provider-services/client/${clientId}`),
  getById: (id) => api.get(`/client-provider-services/${id}`),
  create: (data) => api.post('/client-provider-services', data),
  createForReferral: (referralId, data) => api.post(`/referrals/${referralId}/provider-services`, data),
  update: (id, data) => api.put(`/client-provider-services/${id}`, data),
  delete: (id) => api.delete(`/client-provider-services/${id}`),
  setArchived: (id, archived) => api.patch(`/client-provider-services/${id}/archive`, { archived }),
  getProvidersForEmail: (clientId) => api.get(`/client-provider-services/client/${clientId}/providers-for-email`),
};

// Email API
export const emailAPI = {
  sendTeamEmail: (clientId, templateType) => api.post(`/email/team/${clientId}`, { templateType }),
  clientLetterOpened: (clientId, templateCode) => api.post('/email/client-letter-opened', { clientId, templateCode }),
};

// Email Templates API
export const emailTemplateAPI = {
  /** Same tag catalog as DocGen merge-field reference (use {{TAG}} in email). */
  getMergeTagReference: () => api.get('/email-templates/merge-tag-reference'),
  getAll: (language) => api.get(`/email-templates${language ? `?language=${language}` : ''}`),
  getById: (id) => api.get(`/email-templates/${id}`),
  getByCode: (code, language = 'EN') => api.get(`/email-templates/code/${code}?language=${language}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.patch(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
};

// Milestone / client automation rules (Settings; same permission model as email templates)
export const automationRuleAPI = {
  list: () => api.get('/automation-rules'),
  /** Super-admin only; Client Letters rows for automation rule dropdown */
  getClientLetterTemplates: () => api.get('/automation-rules/client-letter-templates'),
  create: (data) => api.post('/automation-rules', data),
  update: (id, data) => api.patch(`/automation-rules/${id}`, data),
  delete: (id) => api.delete(`/automation-rules/${id}`),
};

// Client Documents API
export const clientDocumentAPI = {
  getTemplate: () => api.get('/client-documents/template'),
  updateTemplate: (data) => api.put('/client-documents/template', data),
  getStorageStatus: () => api.get('/client-documents/storage-status'),
  getClientFileSummary: (clientId) => api.get(`/client-documents/client/${clientId}/file-summary`),
  getClientFiles: (clientId) => api.get(`/client-documents/client/${clientId}/files`),
  getFolderFiles: (clientId, folderKey) => api.get(`/client-documents/client/${clientId}/folder/${encodeURIComponent(folderKey)}/files`),
  uploadFile: (clientId, folderKey, file, saveAsName, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (saveAsName) {
      formData.append('saveAsName', saveAsName);
    }
    return api.post(
      `/client-documents/client/${clientId}/folder/${encodeURIComponent(folderKey)}/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
      }
    );
  },
  downloadFile: (fileId) => api.get(`/client-documents/files/${fileId}/download`, { responseType: 'blob' }),
  getFileVersions: (fileId) => api.get(`/client-documents/files/${fileId}/versions`),
  downloadFileVersion: (fileId, versionId) =>
    api.get(`/client-documents/files/${fileId}/versions/${versionId}/download`, { responseType: 'blob' }),
  insertIntoFile: (fileId, payload) => {
    const formData = new FormData();
    if (payload.insertMode) formData.append('insertMode', payload.insertMode);
    if (payload.pageIndex !== undefined && payload.pageIndex !== null) formData.append('pageIndex', String(payload.pageIndex));
    if (payload.sourceFileId) formData.append('sourceFileId', String(payload.sourceFileId));
    if (payload.sourceFile) formData.append('sourceFile', payload.sourceFile);
    return api.post(`/client-documents/files/${fileId}/insert`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  bulkDownloadFiles: (clientId, fileIds) =>
    api.post(`/client-documents/client/${clientId}/bulk-download`, { fileIds }, { responseType: 'blob' }),
  renameFile: (fileId, newFileName) => api.patch(`/client-documents/files/${fileId}/rename`, { newFileName }),
  moveFileToFolder: (fileId, targetFolderKey) =>
    api.patch(`/client-documents/files/${fileId}/move`, { targetFolderKey }),
  deleteFile: (fileId) => api.delete(`/client-documents/files/${fileId}`),
};

// Credential codes (for provider credentials)
export const credentialCodeAPI = {
  getAll: () => api.get('/credential-codes'),
};

// Service areas (for provider service areas)
export const serviceAreaAPI = {
  getAll: () => api.get('/service-areas'),
};

// Lookup API
export const lookupAPI = {
  getLanguages: () => api.get('/lookups/languages'),
  getRaces: () => api.get('/lookups/races'),
  getTerminationReasons: () => api.get('/lookups/termination-reasons'),
  getCounties: () => api.get('/lookups/counties'),
  getCities: () => api.get('/lookups/cities'),
  getRelationships: () => api.get('/lookups/relationships'),
  getFrequencies: () => api.get('/lookups/frequencies'),
  getDelayReasons: () => api.get('/lookups/delay-reasons'),
  getDsccStatuses: () => api.get('/lookups/dscc-statuses'),
  getTpcLateReasons: () => api.get('/lookups/tpc-late-reasons'),
  getTrackingAlertStatuses: () => api.get('/lookups/tracking-alert-statuses'),
  getProviderTypes: () => api.get('/provider-types'),
  // Referral expansion lookups (via lookup-tables)
  getInsuranceCompanies: () => api.get('/lookup-tables/insurance-companies'),
  getInsuranceTypes: () => api.get('/lookup-tables/insurance-types'),
  getLivingArrangements: () => api.get('/lookup-tables/living-arrangements'),
  getReferralLevel1: () => api.get('/lookup-tables/referral-level-1'),
  getReferralLevel2: () => api.get('/lookup-tables/referral-level-2'),
  getDiagnosisAutoeligible: () => api.get('/lookup-tables/diagnosis-autoeligible'),
};

// Lookup Table Management API (for Settings/Admin)
export const lookupTableAPI = {
  getAll: (tableType, params = {}) => {
    const query = new URLSearchParams();
    if (params.includeInactive === true) query.set('includeInactive', 'true');
    const qs = query.toString();
    return api.get(`/lookup-tables/${tableType}${qs ? `?${qs}` : ''}`);
  },
  create: (tableType, data) => api.post(`/lookup-tables/${tableType}`, data),
  update: (tableType, id, data) => api.put(`/lookup-tables/${tableType}/${id}`, data),
  delete: (tableType, id) => api.delete(`/lookup-tables/${tableType}/${id}`),
};

// Staff API
export const staffAPI = {
  getAll: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/staff${queryString ? `?${queryString}` : ''}`);
  },
  getWorkload: () => api.get('/staff/workload'),
  getById: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  forcePasswordReset: (username) =>
    api.post('/staff/reset-password', { username }),
};

/** Acting SC coverage (temporary caseload). Manage routes require caseload.manage_acting_sc_coverage. */
export const actingScCoverageAPI = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/acting-sc-coverage${queryString ? `?${queryString}` : ''}`);
  },
  myCovering: () => api.get('/acting-sc-coverage/my-covering'),
  create: (data) => api.post('/acting-sc-coverage', data),
  cancel: (id) => api.post(`/acting-sc-coverage/${encodeURIComponent(id)}/cancel`),
};

export const coverageAPI = {
  getActive: () => api.get('/coverage/active'),
  getClientsForSc: (staffId) => api.get(`/coverage/sc/${staffId}/clients`),
  createAssignments: (data) => api.post('/coverage/assignments', data),
  removeAssignment: (id) => api.delete(`/coverage/assignments/${id}`),
};

// Reports API
export const reportsAPI = {
  get: (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/reports/${endpoint}${queryString ? `?${queryString}` : ''}`;
    return api.get(url);
  },
  exportCsv: (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/reports/${endpoint}${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob',
    });
  },
  post: (endpoint, data = {}) => {
    return api.post(`/reports/${endpoint}`, data);
  },
  canonicalDefinition: (reportId) => {
    return api.get(`/reports/canonical/${reportId}/definition`);
  },
  canonicalQuery: (reportId, payload = {}) => {
    return api.post(`/reports/canonical/${reportId}/query`, payload);
  },
  canonicalExportCsv: (reportId, payload = {}) => {
    return api.post(`/reports/canonical/${reportId}/export.csv`, payload, {
      responseType: 'blob',
    });
  },
  /** POST body { filters }; returns { emails, count }. For Search Providers BCC list. */
  canonicalSearchProvidersEmails: (payload = {}) => api.post('/reports/canonical/search-providers/emails', payload),
  updateClientStatus: (id, data) => {
    return api.patch(`/reports/client-status/${id}`, data);
  },
  /** POST ClientEIT CSV for TPC Access vs Cornerstone. FormData with field "file". */
  uploadTpcCornerstone: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reports/tpc-access-vs-cornerstone/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** POST chldeild CSV/TXT for Exit Access vs Cornerstone. FormData with field "file". */
  uploadExitCornerstone: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reports/exit-access-vs-cornerstone/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  serviceDelayAutoResolve: () => api.post('/reports/service-delay-workflow/actions/auto-resolve-aged-out'),
  serviceDelayUpdateRow: (id, payload = {}) => api.patch(`/reports/service-delay-workflow/rows/${id}`, payload),
  serviceDelayExportCsv: (payload = {}) =>
    api.post('/reports/service-delay-workflow/export.csv', payload, { responseType: 'blob' }),
};

/** Primary-contact email lists for clients with an open referral (Lists → Mailing List Builder). Requires mailing_list_builder.use. */
export const mailingListBuilderAPI = {
  getOptions: () => api.get('/mailing-list-builder/options'),
  search: (body) => api.post('/mailing-list-builder/search', body),
};

// Work Tasks API
export const workTasksAPI = {
  getTasks: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/work-tasks${queryString ? `?${queryString}` : ''}`);
  },
  /** Admin/manager: notify all or selected staff (inbox on dashboard). Requires notify_staff.send. */
  broadcastStaffNotification: (body) => api.post('/work-tasks/staff-notifications/broadcast', body),
  getCounts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/work-tasks/counts${queryString ? `?${queryString}` : ''}`);
  },
  getRules: () => api.get('/work-tasks/rules'),
  markComplete: (milestoneId, completedDate = null) => {
    const body = {};
    if (completedDate) {
      body.completed_date = completedDate;
    }
    return api.post(`/work-tasks/${milestoneId}/complete`, body);
  },
  getInboxItems: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/work-tasks/inbox${queryString ? `?${queryString}` : ''}`);
  },
  createInboxItem: (data) => api.post('/work-tasks/inbox', data),
  updateInboxItemStatus: (id, status) => api.patch(`/work-tasks/inbox/${id}`, { status }),
};

// Monthly Contact API (Option C: inbox reminder + dedicated view + manager counts)
export const monthlyContactAPI = {
  getNeeded: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/monthly-contact/needed${queryString ? `?${queryString}` : ''}`);
  },
  /** List clients whose last contact was by the given method (meeting, phone, email_personal, etc.). */
  getByMethod: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/monthly-contact/by-method${queryString ? `?${queryString}` : ''}`);
  },
  /** Email templates for Monthly Contact dropdown (uses work_tasks.view so View-as-SC works). */
  getEmailTemplates: () => api.get('/monthly-contact/email-templates'),
  getCounts: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/monthly-contact/counts${queryString ? `?${queryString}` : ''}`);
  },
  createReminders: () => api.post('/monthly-contact/remind'),
  preview: (params) => {
    const q = new URLSearchParams(params).toString();
    return api.get(`/monthly-contact/preview?${q}`);
  },
  sendAll: (body) => api.post('/monthly-contact/send-all', body),
  recordContact: (body) => api.post('/monthly-contact/record-contact', body),
};

// Referral API
export const referralAPI = {
  getByClientId: (clientId) => api.get(`/referrals/client/${clientId}`),
  getByOpenDate: (params) => api.get('/referrals/by-open-date', { params }),
  getById: (id) => api.get(`/referrals/${id}`),
  getProviderToFromText: (referralId) => api.get(`/referrals/${referralId}/provider-to-from-text`),
  getMilestones: (referralId) => api.get(`/referrals/${referralId}/milestones`),
  updateMilestoneTrackingStatus: (referralId, milestoneId, body) =>
    api.patch(`/referrals/${referralId}/milestones/${milestoneId}`, body),
  /** Set or clear completion date; body may include completed_date (YYYY-MM-DD or null) */
  updateMilestoneCompletedDate: (referralId, milestoneId, completedDate) =>
    api.patch(`/referrals/${referralId}/milestones/${milestoneId}`, { completed_date: completedDate || null }),
  /** Annual extended dates, EIES, TPC late reason, sc_only_no_service_expected (narrow permission) */
  patchReferralTracking: (referralId, body) => api.patch(`/referrals/${referralId}/tracking`, body),
  create: (data) => api.post('/referrals', data),
  update: (id, data) => api.put(`/referrals/${id}`, data),
  close: (id, data) => api.post(`/referrals/${id}/close`, data),
  updateTermination: (id, data) => api.patch(`/referrals/${id}/termination`, data),
  patchStatus: (id, status) => api.patch(`/referrals/${id}/status`, { status }),
  getInsuranceWorkspace: (referralId) => api.get(`/referrals/${referralId}/insurance-workspace`),
  putInsuranceWorkspace: (referralId, body) => api.put(`/referrals/${referralId}/insurance-workspace`, body),
  getIncompleteCornerstoneList: () => api.get('/referrals/incomplete-cornerstone-list'),
  getCornerstoneDetail: (referralId) => api.get(`/referrals/${referralId}/cornerstone-detail`),
};

// Checklist API (feature-gated; referral-scoped)
export const checklistAPI = {
  getTypes: () => api.get('/checklists/types'),
  getMine: (params) => api.get('/checklists/mine', { params }),
  getCount: (params) => api.get('/checklists/count', { params }),
  listByReferralId: (referralId) => api.get(`/checklists/referral/${referralId}`),
  createForReferral: (referralId, body) => api.post(`/checklists/referral/${referralId}`, body),
  getById: (id) => api.get(`/checklists/${id}`),
  update: (id, body) => api.patch(`/checklists/${id}`, body),
  delete: (id) => api.delete(`/checklists/${id}`),
  updateItem: (checklistId, itemId, body) => api.patch(`/checklists/${checklistId}/items/${itemId}`, body),
  // Admin: template management
  getAdminWorkTaskOptions: () => api.get('/checklists/admin/work-task-options'),
  createType: (body) => api.post('/checklists/admin/types', body),
  getTypeWithItems: (typeId) => api.get(`/checklists/admin/types/${typeId}`),
  updateType: (typeId, body) => api.patch(`/checklists/admin/types/${typeId}`, body),
  createItemTemplate: (typeId, body) => api.post(`/checklists/admin/types/${typeId}/items`, body),
  updateItemTemplate: (itemId, body) => api.patch(`/checklists/admin/items/${itemId}`, body),
  deleteItemTemplate: (itemId) => api.delete(`/checklists/admin/items/${itemId}`),
};

// Transfers API (CFC outbound transfer tracking)
export const transferAPI = {
  list: (params) => api.get('/transfers', { params }),
  getOptionsReceivingCfc: () => api.get('/transfers/options/receiving-cfc'),
  getById: (id) => api.get(`/transfers/${id}`),
  create: (data) => api.post('/transfers', data),
  update: (id, data) => api.patch(`/transfers/${id}`, data),
};

// File Review API (feature-gated)
export const fileReviewAPI = {
  getTemplate: () => api.get('/file-review/template'),
  createTemplateRow: (body) => api.post('/file-review/template', body),
  updateTemplateRow: (id, body) => api.patch(`/file-review/template/${id}`, body),
  deleteTemplateRow: (id) => api.delete(`/file-review/template/${id}`),
  listReviews: (params) => api.get('/file-review/reviews', { params }),
  getReview: (id) => api.get(`/file-review/reviews/${id}`),
  createReview: (body) => api.post('/file-review/reviews', body),
  updateReview: (id, body) => api.patch(`/file-review/reviews/${id}`, body),
  updateReviewItem: (reviewId, itemId, body) => api.patch(`/file-review/reviews/${reviewId}/items/${itemId}`, body),
};

// Training API (feature-gated)
export const trainingAPI = {
  listWindows: (params) => api.get('/training/windows', { params: params || {} }),
  getWindowById: (id) => api.get(`/training/windows/${id}`),
  getWindowByStaff: (staffId) => api.get(`/training/windows/by-staff/${staffId}`),
  upsertWindow: (body) => api.put('/training/windows', body),
  listActivities: (params) => api.get('/training/activities', { params: params || {} }),
  getActivity: (id) => api.get(`/training/activities/${id}`),
  createActivity: (body) => api.post('/training/activities', body),
  updateActivity: (id, body) => api.patch(`/training/activities/${id}`, body),
  deleteActivity: (id) => api.delete(`/training/activities/${id}`),
  reportActivity: (params) => api.get('/training/reports/activity', { params: params || {} }),
  reportMonitor: (params) => api.get('/training/reports/monitor', { params }),
  listCredentials: (params) => api.get('/training/credentials', { params: params || {} }),
  getCredentialsByStaff: (staffId) => api.get(`/training/credentials/by-staff/${staffId}`),
  upsertCredentials: (body) => api.put('/training/credentials', body),
  listTempTraining: (params) => api.get('/training/temp-training', { params: params || {} }),
  getTempTrainingByStaff: (staffId) => api.get(`/training/temp-training/by-staff/${staffId}`),
  upsertTempTraining: (body) => api.put('/training/temp-training', body),
  listFullCredentialWindows: () => api.get('/training/full-credential-windows'),
  notifyRecredential: (staffIds) => api.post('/training/notify-recredential', { staff_ids: staffIds }),
};

// Data Corrections API (feature-gated)
export const dataCorrectionsAPI = {
  list: (params) => api.get('/data-corrections', { params: params || {} }),
  getById: (id) => api.get(`/data-corrections/${id}`),
  create: (body) => api.post('/data-corrections', body),
  update: (id, body) => api.patch(`/data-corrections/${id}`, body),
  getMergePayload: (id) => api.get(`/data-corrections/merge-payload/${id}`),
};

// Client Services API (Provider Search: needs + provider search + central availability)
export const clientServicesAPI = {
  listNeeds: (params) => api.get('/client-services/needs', { params }),
  getNeed: (id) => api.get(`/client-services/needs/${id}`),
  createNeed: (body) => api.post('/client-services/needs', body),
  updateNeed: (id, body) => api.patch(`/client-services/needs/${id}`, body),
  reclassifyNeed: (id, issueType) => api.patch(`/client-services/needs/${id}/reclassify`, { issue_type: issueType }),
  addWaitingToTeam: (id) => api.post(`/client-services/needs/${id}/add-waiting`),
  searchProviders: (params) => api.get('/client-services/providers/search', { params }),
  searchProvenProvidersFromNeeds: (params) => api.get('/client-services/providers/proven-needs-search', { params }),
  getCredentialCodes: () => api.get('/client-services/credential-codes'),
  listCentralAvailability: (params) => api.get('/client-services/central-availability', { params }),
  getCentralAvailability: (id) => api.get(`/client-services/central-availability/${id}`),
  createCentralAvailability: (body) => api.post('/client-services/central-availability', body),
  updateCentralAvailability: (id, body) => api.patch(`/client-services/central-availability/${id}`, body),
  deleteCentralAvailability: (id) => api.delete(`/client-services/central-availability/${id}`),
  matchCentralAvailability: (params) => api.get('/client-services/central-availability/match', { params }),
  prepareCentralAvailabilityEmail: (params) => api.get('/client-services/central-availability/prepare-email', { params }),
  sendCentralAvailabilityEmail: (body) => api.post('/client-services/central-availability/send-email', body),
};

// Tenant Settings API (buffers, paths, contact, AT coordinator — loaded once per request)
export const tenantSettingsAPI = {
  get: () => api.get('/tenant-settings'),
  update: (data) => api.patch('/tenant-settings', data),
  getJobSchedules: () => api.get('/tenant-settings/job-schedules'),
};

// Auth API (public - no token required)
export const authAPI = {
  getConfig: () => axios.get(`${API_BASE_URL}/auth/config`),
  /** Discover flow: no org list; backend resolves tenant after Microsoft callback */
  getMicrosoftRedirectUrlDiscover: () => `${API_BASE_URL}/auth/microsoft`,
  getMicrosoftRedirectUrl: (tenantCode) =>
    `${API_BASE_URL}/auth/microsoft?tenant_code=${encodeURIComponent(tenantCode)}`,
  getTenantChoice: (code) =>
    axios.get(`${API_BASE_URL}/auth/microsoft/tenant-choice`, { params: { code } }),
  postMicrosoftComplete: (code, tenantCode) =>
    axios.post(`${API_BASE_URL}/auth/microsoft/complete`, { code, tenant_code: tenantCode }),
  login: (credentials) => {
    // Create a separate axios instance for login (no auth headers)
    return axios.post(`${API_BASE_URL}/auth/login`, credentials);
  },
  setInitialPassword: (username, password) => {
    return axios.post(`${API_BASE_URL}/auth/set-initial-password`, { username, password });
  },
  setPassword: (password) => {
    return api.post('/auth/set-password', { password });
  },
};

// Role & Permission Management API
export const rolePermissionAPI = {
  getAllRoles: () => api.get('/role-permissions/roles'),
  createRole: (data) => api.post('/role-permissions/roles', data),
  updateRole: (roleId, data) => api.patch(`/role-permissions/roles/${roleId}`, data),
  deleteRole: (roleId) => api.delete(`/role-permissions/roles/${roleId}`),
  getAllPermissions: () => api.get('/role-permissions/permissions'),
  getRolePermissions: (roleId) => api.get(`/role-permissions/roles/${roleId}/permissions`),
  updateRolePermissions: (roleId, permissionIds) =>
    api.put(`/role-permissions/roles/${roleId}/permissions`, { roleId, permissionIds }),
};

// Tenant users (list for "View as user" and Team / assign roles)
export const tenantUsersAPI = {
  getList: (params) => api.get('/tenant-users', { params: params || {} }),
  getEffectivePermissions: (userId) => api.get(`/tenant-users/${userId}/effective-permissions`),
  addUser: (data) => api.post('/tenant-users', data),
  addRole: (userId, roleId) => api.post(`/tenant-users/${userId}/roles`, { role_id: roleId }),
  removeRole: (userId, roleId) => api.delete(`/tenant-users/${userId}/roles/${roleId}`),
};

// Super Admin API (system-level, requires DEVELOPER role)
export const superAdminAPI = {
  // User avatars (super admin sets/removes any user's photo)
  uploadUserAvatar: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/super-admin/users/${id}/avatar`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteUserAvatar: (id) => api.delete(`/super-admin/users/${id}/avatar`),
  // Organizations
  getAllOrgs: () => api.get('/super-admin/orgs'),
  getOrgById: (id) => api.get(`/super-admin/orgs/${id}`),
  createOrg: (data) => api.post('/super-admin/orgs', data),
  updateOrg: (id, data) => api.patch(`/super-admin/orgs/${id}`, data),
  deleteOrg: (id) => api.delete(`/super-admin/orgs/${id}`),
  getOrgTenants: (orgId) => api.get(`/super-admin/orgs/${orgId}/tenants`),
  
  // Tenants
  getAllTenants: () => api.get('/super-admin/tenants'),
  getTenantById: (id) => api.get(`/super-admin/tenants/${id}`),
  createTenant: (data) => api.post('/super-admin/tenants', data),
  updateTenant: (id, data) => api.patch(`/super-admin/tenants/${id}`, data),
  updateTenantSettings: (id, data) => api.patch(`/super-admin/tenants/${id}/settings`, data),
  deleteTenant: (id) => api.delete(`/super-admin/tenants/${id}`),
  
  // Users
  getAllUsers: () => api.get('/super-admin/users'),
  getUserById: (id) => api.get(`/super-admin/users/${id}`),
  createUser: (data) => api.post('/super-admin/users', data),
  updateUser: (id, data) => api.patch(`/super-admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/super-admin/users/${id}`),
  
  // Staff (for user creation - get all staff across tenants)
  getAllStaff: () => api.get('/super-admin/staff'),

  // Roles (for membership assignment; optional tenant_id for filtered list)
  getAllRoles: (tenantId) =>
    api.get('/super-admin/roles', tenantId != null ? { params: { tenant_id: tenantId } } : {}),

  // Tenant memberships (stackable: add/remove one role at a time)
  getUserMemberships: (userId) => api.get(`/super-admin/users/${userId}/memberships`),
  addUserMembership: (userId, data) => api.post(`/super-admin/users/${userId}/memberships`, data),
  removeUserMembership: (userId, tenantId, roleId) =>
    api.delete(`/super-admin/users/${userId}/memberships/${tenantId}/${roleId}`),
  disableUserMembership: (userId, tenantId) => api.delete(`/super-admin/users/${userId}/memberships/${tenantId}`),

  // Billing (commercial seats, QBO metadata, snapshots)
  getBillingSummary: () => api.get('/super-admin/billing/summary'),
  getTenantBillingProfile: (tenantId) => api.get(`/super-admin/tenants/${tenantId}/billing-profile`),
  patchTenantBillingProfile: (tenantId, data) => api.patch(`/super-admin/tenants/${tenantId}/billing-profile`, data),
  getTenantBillingUsers: (tenantId) => api.get(`/super-admin/tenants/${tenantId}/billing-users`),
  patchTenantUserBilling: (tenantId, userId, data) =>
    api.patch(`/super-admin/tenants/${tenantId}/billing-users/${userId}`, data),
  getTenantBillingSnapshots: (tenantId, params) =>
    api.get(`/super-admin/tenants/${tenantId}/billing-snapshots`, { params }),
  createTenantBillingSnapshot: (tenantId, data) =>
    api.post(`/super-admin/tenants/${tenantId}/billing-snapshots`, data),

  // Scheduled jobs
  listJobConfigs: () => api.get('/super-admin/jobs/config'),
  upsertJobConfig: (tenantId, jobCode, data) =>
    api.put(`/super-admin/jobs/config/${tenantId}/${jobCode}`, data),
  listJobLog: (params) => api.get('/super-admin/jobs/log', { params }),
  runJob: (data) => api.post('/super-admin/jobs/run', data),
};

export const tenantBillingAPI = {
  getSummary: () => api.get('/tenant-billing/summary'),
};

export { api };
export default api;
